import { useRef, useState } from "react";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { agentKeys, useAgents, usePublishAgent, type AgentPatchVars } from "@/shared/api/agents";
import { ApiError } from "@/shared/api/errors";

import type { Agent } from "@/shared/types";

/** Publish refused a Needs approval name the agent doesn't have. */
const NEEDS_APPROVAL_UNKNOWN = 3060009;

/** Why a publish failed, in words: for 3060009 the names, from the error's details. */
function whyNotPublished(err: unknown): string {
  if (!(err instanceof Error)) return "Something went wrong";
  if (!(err instanceof ApiError) || err.code !== NEEDS_APPROVAL_UNKNOWN) return err.message;
  const unknown = (err.details ?? []).flatMap((d) => (Array.isArray(d.unknown) ? d.unknown.map(String) : []));
  if (!unknown.length) return err.message;
  return `Needs approval lists ${unknown.join(", ")}, which this agent doesn't have — remove ${unknown.length > 1 ? "them" : "it"}, or link the app or turn on the switch ${unknown.length > 1 ? "they come" : "it comes"} from`;
}

/* THE PUBLISH ACTION — a server agent's, from the Publish bar in its Settings.

   An edit only changes the draft; runs read the published version. So a model,
   connection, max tokens or capability picked anywhere reaches a run through
   here and nowhere else. It is always available, because the server answers
   200 "nothing changed" when the draft already matches.

   Never twice at once: a double click published twice. And never ahead of an
   edit still being written — Settings sends its edits without waiting, and a
   publish that overtook one would freeze the draft without it.

   `changed` is what the Publish button waits for: the server says the
   draft differs from the published version, or an edit is still on its way there
   (the list is re-read before that edit counts as settled, so the two hand over
   without a gap). */
export default function usePublish(agent: Pick<Agent, "id" | "version"> | null | undefined) {
  const qc = useQueryClient();
  const publishApi = usePublishAgent();
  const running = useRef(false);
  const [publishing, setPublishing] = useState(false);

  const patchFilter = {
    mutationKey: agentKeys.patch(),
    predicate: (m: { state: { variables: unknown } }) => (m.state.variables as AgentPatchVars | undefined)?.id === agent?.id,
  };
  const writing = () => qc.isMutating(patchFilter) > 0;
  const { data: rows } = useAgents();
  const changed = useIsMutating(patchFilter) > 0 || !!rows?.find((row) => row.id === agent?.id)?.has_unpublished_changes;
  const drafted = () => new Promise<void>((resolve) => {
    if (!writing()) return resolve();
    const stop = qc.getMutationCache().subscribe(() => {
      if (!writing()) { stop(); resolve(); }
    });
  });

  const publishDraft = async ({ id: agentId, version }: Pick<Agent, "id" | "version">) => {
    await drafted();
    try {
      const result = await publishApi.mutateAsync(agentId);
      const id = `v${result.published.version}`;
      if (result.created) toast.success(`Published ${id}`, { description: "New runs use this version." });
      else toast.success("Nothing changed", { description: `${id} is still the published version.` });
    } catch (err) {
      // Silent in the mutation cache: the refusal is said here, with the names it lists.
      const ref = err instanceof ApiError && err.request_id ? ` · ref ${err.request_id}` : "";
      toast.error("Not published", { description: `${whyNotPublished(err)}${ref}. New runs still use ${version}.` });
    }
  };

  const publish = async () => {
    if (!agent || running.current) return;
    running.current = true;
    setPublishing(true);
    try {
      await publishDraft(agent);
    } finally {
      running.current = false;
      setPublishing(false);
    }
  };

  return { publish, publishing, changed };
}
